import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { supabase } from "../../lib/supabaseClient";
import { useAppSelector } from "../../hooks";
import FilmCard from "../../components/FilmCard/FilmCard";
import {
  useGetProfileQuery,
  useGetProfileFilmsQuery,
  useGetFollowStatsQuery,
  useLazyGetFollowListQuery,
  useToggleFollowMutation,
} from "../../services/api";
import defaultUserAvatar from "../../assets/defaultuser-avatar.svg";

type UserProfile = {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  bio: string;
  created_at: string;
};

type Film = any;

const Profile: React.FC = () => {
  const { username } = useParams<{ username?: string }>();
  const navigate = useNavigate();
  const authUser = useAppSelector((s) => s.auth.user);

  const [activeTab, setActiveTab] = useState<"currently" | "watched" | "favorite" | "will_watch">("currently");
  const [isEditing, setIsEditing] = useState(false);
  const [editBio, setEditBio] = useState("");
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);

  const { data: profileData, isLoading: profileLoading, refetch: refetchProfile } = useGetProfileQuery(
    { username: username, userId: !username && authUser ? authUser.id : undefined },
    { skip: !username && !authUser }
  );
  const profile = profileData;

  const { data: filmsData, isLoading: filmsLoading } = useGetProfileFilmsQuery(
    { userId: profile?.id, status: activeTab },
    { skip: !profile }
  );
  const films = filmsData || [];

  const { data: followStats } = useGetFollowStatsQuery(
    { userId: profile?.id, currentUserId: authUser?.id },
    { skip: !profile }
  );

  const [triggerFollowList, { data: followListData }] = useLazyGetFollowListQuery();
  const [toggleFollow, { isLoading: isTogglingFollow }] = useToggleFollowMutation();

  const isOwnProfile = authUser && (!username || profile?.id === authUser.id);
  const isFollowing = followStats?.isFollowing || false;
  const followersCount = followStats?.followers || 0;
  const followingCount = followStats?.following || 0;

  // Sync bio when profile loads
  useEffect(() => {
    if (profile) {
      setEditBio(profile.bio || "");
    }
  }, [profile]);





  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!authUser || !profile) {
      alert("Please sign in to follow users");
      return;
    }
    try {
      await toggleFollow({
        targetUserId: profile.id,
        currentUserId: authUser.id,
        isFollowing,
      }).unwrap();
    } catch (err: any) {
      console.error("Follow error:", err);
      alert(err.message || "Failed to update follow status");
    }
  };

  const handleSaveBio = async () => {
    if (!profile || !isOwnProfile) return;

    const { error } = await supabase.from("user_profiles").update({ bio: editBio }).eq("id", profile.id);

    if (!error) {
      refetchProfile(); // Refresh profile data
      setIsEditing(false);
    }
  };



  if (profileLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-950">
        <div className="text-white text-xl">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 p-4">
        <h2 className="text-2xl text-white mb-4">Profile not found</h2>
        <button onClick={() => navigate("/")} className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700">
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-950 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-gray-900 border-2 border-red-600 rounded-xl p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <img
              src={profile.avatar_url || "https://placehold.co/400x400/1f2937/ffffff/png?text=No+Avatar"}
              alt={profile.display_name}
              className="w-32 h-32 rounded-full border-4 border-red-600 object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "https://placehold.co/400x400/1f2937/ffffff/png?text=No+Avatar";
              }}
            />

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">{profile.display_name}</h1>
                  <p className="text-gray-400">@{profile.username}</p>
                </div>

                {/* Follow Button - Only show on other users' profiles */}
                {!isOwnProfile && authUser && (
                  <button
                    onClick={handleFollowToggle}
                    disabled={isTogglingFollow}
                    className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                      isFollowing ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
                    } disabled:opacity-50`}
                  >
                    {isTogglingFollow ? "..." : isFollowing ? "Unfollow" : "Follow"}
                  </button>
                )}
              </div>

              {/* Followers/Following Stats - Make clickable */}
              <div className="flex gap-6 mb-4">
                <button
                  onClick={() => {
                    triggerFollowList({ userId: profile.id, type: "followers" });
                    setShowFollowersModal(true);
                  }}
                  className="hover:opacity-80 transition-opacity text-left"
                >
                  <span className="text-2xl font-bold text-white">{followersCount}</span>
                  <p className="text-sm text-gray-400">Followers</p>
                </button>
                <button
                  onClick={() => {
                    triggerFollowList({ userId: profile.id, type: "following" });
                    setShowFollowingModal(true);
                  }}
                  className="hover:opacity-80 transition-opacity text-left"
                >
                  <span className="text-2xl font-bold text-white">{followingCount}</span>
                  <p className="text-sm text-gray-400">Following</p>
                </button>
              </div>

              {/* Bio */}
              {isEditing ? (
                <div className="mb-4">
                  <textarea
                    value={editBio}
                    onChange={(e) => setEditBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border-2 border-gray-700 focus:border-red-600 focus:outline-none text-white resize-none"
                    rows={3}
                    maxLength={200}
                  />
                  <div className="flex gap-2 mt-2">
                    <button onClick={handleSaveBio} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditBio(profile.bio || "");
                      }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-gray-300">{profile.bio || "No bio yet"}</p>
                  {isOwnProfile && (
                    <button onClick={() => setIsEditing(true)} className="text-red-500 text-sm mt-2 hover:text-red-400">
                      Edit bio
                    </button>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-500">Joined {new Date(profile.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b-2 border-gray-800 overflow-x-auto">
          <button
            onClick={() => setActiveTab("currently")}
            className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
              activeTab === "currently" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400 hover:text-white"
            }`}
          >
            Currently Watching
          </button>
          <button
            onClick={() => setActiveTab("watched")}
            className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
              activeTab === "watched" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400 hover:text-white"
            }`}
          >
            Watched
          </button>
          <button
            onClick={() => setActiveTab("favorite")}
            className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
              activeTab === "favorite" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400 hover:text-white"
            }`}
          >
            Favorites
          </button>
          <button
            onClick={() => setActiveTab("will_watch")}
            className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
              activeTab === "will_watch" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400 hover:text-white"
            }`}
          >
            Watchlist
          </button>
        </div>

        {/* Films Grid */}
        {filmsLoading ? (
           <div className="text-center text-gray-500 py-16">Loading films...</div>
        ) : films.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            <p className="text-lg">No films in this category yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {films.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        )}
      </div>

      {/* Followers Modal */}
      {showFollowersModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowFollowersModal(false)}
        >
          <div
            className="bg-gray-900 border-2 border-red-600 rounded-xl max-w-md w-full max-h-[70vh] overflow-y-auto p-6 custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Followers</h2>
              <button onClick={() => setShowFollowersModal(false)} className="text-gray-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {followListData && followListData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No followers yet</p>
            ) : (
              <div className="space-y-3">
                {followListData?.map((user: any) => (
                  <div
                    key={user.id}
                    onClick={() => {
                      setShowFollowersModal(false);
                      navigate(`/profile/${user.username}`);
                    }}
                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <img
                      src={user.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                      alt={user.username}
                      className="w-12 h-12 rounded-full border-2 border-red-600"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                      }}
                    />
                    <div>
                      <p className="text-white font-semibold">{user.display_name}</p>
                      <p className="text-sm text-gray-400">@{user.username}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Following Modal */}
      {showFollowingModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowFollowingModal(false)}
        >
          <div
            className="bg-gray-900 border-2 border-red-600 rounded-xl max-w-md w-full max-h-[70vh] overflow-y-auto p-6 custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Following</h2>
              <button onClick={() => setShowFollowingModal(false)} className="text-gray-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {followListData && followListData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Not following anyone yet</p>
            ) : (
              <div className="space-y-3">
                {followListData?.map((user: any) => (
                  <div
                    key={user.id}
                    onClick={() => {
                      setShowFollowingModal(false);
                      navigate(`/profile/${user.username}`);
                    }}
                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <img
                      src={user.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                      alt={user.username}
                      className="w-12 h-12 rounded-full border-2 border-red-600"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                      }}
                    />
                    <div>
                      <p className="text-white font-semibold">{user.display_name}</p>
                      <p className="text-sm text-gray-400">@{user.username}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
